import {queryFnFetch} from '@github-ui/react-core/future/query-fn-fetch'
import {getListItemIdentity} from '@github-ui/shared-dashboard-lists/shared/helpers/item-identity'

import {ISSUES_DASHBOARD_PATH} from '../constants/routes'
import {enrichIssueItem, enrichPullItem} from '../helpers/merge-deferred-issues'
import type {IssuesDashboardEnrichedDataPayload, IssuesDashboardRowsPayload} from '../types/routes-types'

type EnrichedRow = IssuesDashboardEnrichedDataPayload['issues'][number]
type RowMain = IssuesDashboardRowsPayload['payload']['issuesDashboardSurfaceRowsData']['results'][number]
type RowDeferred = IssuesDashboardRowsPayload['payload']['issuesDashboardSurfaceRowsData']['deferred'][number]

// Query-key prefix for the per-row "node" queries. Each visible row gets a node
// query seeded from the list (root) query; a live update invalidates just that
// row's node, refreshing one row instead of refetching the whole list.
export const ROW_NODE_QUERY_PREFIX = 'issues-dashboard-row-node'

// Mirrors the backend `MAX_ROW_IDS` cap on GET /issues/_rows. The
// server silently truncates ids past this, so the client chunks its batched
// request to this size — an oversized batch never drops rows.
export const MAX_ROWS_PER_REQUEST = 50

export function chunk<T>(items: T[], size: number): T[][] {
  const chunks: T[][] = []
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size))
  }
  return chunks
}

// Stable per-row identity. Used for both the node query key and the
// alive-subscriber render key so they always agree. See `getListItemIdentity`.
export function rowNodeKey(item: {itemType: string; id: number | bigint | string; globalId?: string}): string {
  return getListItemIdentity(item)
}

export function rowNodeQueryKey(key: string): [string, string] {
  return [ROW_NODE_QUERY_PREFIX, key]
}

export function enrichRow(main: RowMain, deferred?: RowDeferred): EnrichedRow {
  if (main.itemType === 'pull_request') {
    return enrichPullItem(main, deferred?.itemType === 'pull_request' ? deferred : undefined)
  }
  return enrichIssueItem(main, deferred?.itemType === 'issue' ? deferred : undefined)
}

function compositeKey(item: {itemType: string; id: number | string}): string {
  return `${item.itemType}:${item.id}`
}

async function fetchDashboardRowNodes(globalIds: string[]): Promise<Map<string, EnrichedRow>> {
  const data = await queryFnFetch<IssuesDashboardRowsPayload>({
    queryDeps: {
      pathname: `${ISSUES_DASHBOARD_PATH}/_rows`,
      searchParams: {ids: globalIds.join(',')},
    },
  })
  const node = data.payload?.issuesDashboardSurfaceRowsData
  const deferredByKey = new Map<string, RowDeferred>()
  for (const deferred of node?.deferred ?? []) {
    deferredByKey.set(compositeKey(deferred), deferred)
  }
  const byGlobalId = new Map<string, EnrichedRow>()
  for (const main of node?.results ?? []) {
    byGlobalId.set(getListItemIdentity(main), enrichRow(main, deferredByKey.get(compositeKey(main))))
  }
  return byGlobalId
}

type PendingResolver<T> = {
  resolve: (value: T | undefined) => void
  reject: (error: unknown) => void
}

// Collection window before the first fetch fires. Alive events for a bulk edit
// arrive in separate WebSocket frames (separate ticks), so a 0ms/next-tick
// window would fetch one row per event. Waiting this long lets events that land
// close together coalesce into a single `/_rows` request, bounding the request
// rate during bulk updates. Row refreshes don't need to be instant, so we favor
// grouping. (In-flight arrivals are additionally absorbed by the drain loop.)
const BATCH_WINDOW_MS = 1000

/**
 * Build a row-node loader that batches `_rows` fetches. The queue is the same for
 * every surface; only the fetch differs, so each surface passes its own
 * `fetchRowNodes` and gets an isolated queue.
 */
export function createRowNodeLoader<T>(fetchRowNodes: (globalIds: string[]) => Promise<Map<string, T>>): {
  loadRowNode: (globalId: string) => Promise<T | undefined>
} {
  // Serial fetch queue. Coalesce row-node fetches triggered within a tick, then
  // fetch ONE batch at a time — never in parallel. Ids that arrive while a fetch
  // is in flight are picked up by the next loop iteration, so a slow request
  // naturally absorbs everything queued behind it instead of firing overlapping
  // requests at the server.
  let pending = new Map<string, Array<PendingResolver<T>>>()
  let scheduled = false
  let draining = false

  function scheduleDrain(): void {
    // While draining, the loop already picks up newly-queued ids, so no new timer.
    if (scheduled || draining) return
    scheduled = true
    // Collect ids arriving within the window before the first fetch, so a burst
    // of per-row live updates batches instead of firing one request each.
    setTimeout(() => {
      scheduled = false
      void drain()
    }, BATCH_WINDOW_MS)
  }

  async function drain(): Promise<void> {
    if (draining) return
    draining = true
    try {
      while (pending.size > 0) {
        const batch = pending
        pending = new Map()
        // One request at a time: chunk to the server cap and fetch sequentially.
        for (const chunkIds of chunk([...batch.keys()], MAX_ROWS_PER_REQUEST)) {
          try {
            const byGlobalId = await fetchRowNodes(chunkIds)
            for (const globalId of chunkIds) {
              for (const resolver of batch.get(globalId) ?? []) resolver.resolve(byGlobalId.get(globalId))
            }
          } catch (error) {
            // Isolate failures per chunk so one bad request doesn't reject the rest.
            for (const globalId of chunkIds) {
              for (const resolver of batch.get(globalId) ?? []) resolver.reject(error)
            }
          }
        }
      }
    } finally {
      draining = false
    }
  }

  return {
    loadRowNode(globalId: string): Promise<T | undefined> {
      return new Promise<T | undefined>((resolve, reject) => {
        const resolvers = pending.get(globalId) ?? []
        resolvers.push({resolve, reject})
        pending.set(globalId, resolvers)
        scheduleDrain()
      })
    },
  }
}

// The dashboard's row-node loader, over GET /issues/_rows.
export const {loadRowNode} = createRowNodeLoader<EnrichedRow>(fetchDashboardRowNodes)

export type RowSettleTarget = {queryKey: [string, string]; revalidate: true}

/**
 * The row-node caches a settling mutation has to fold into.
 *
 * Every row with a live-update channel has its own node cache, and `useEnrichedIssues` prefers that
 * node over the list row whenever the node was refetched more recently. Folding preserves
 * `dataUpdatedAt`, so a node cache left unfolded keeps winning and shows the pre-mutation value
 * after the overlay drops. Registering only the row the side panel currently shows is therefore not
 * enough: an edit can confirm after the panel has moved on, or with no panel open at all.
 */
export function rowNodeSettleTargets(
  rows: ReadonlyArray<{itemType: string; id: number | string; globalId?: string}>,
  panelRowKey?: string,
): RowSettleTarget[] {
  const keys = rows.filter(row => row.itemType === 'issue').map(rowNodeKey)
  // The panel can show a row this page doesn't list — a deep link, or a page change while open.
  if (panelRowKey && !keys.includes(panelRowKey)) keys.push(panelRowKey)
  return [...new Set(keys)].map(key => ({queryKey: rowNodeQueryKey(key), revalidate: true}))
}
